import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { Keyboard } from 'react-native';
import {
    ControlledInput,
    ControlledInputTrigger,
    ControlledMultilineInput,
    Form,
    USE_FORM_MODE_DEFAULTS,
    YStack,
} from 'smoothie';
import { CheckboxRowInsetList } from 'smoothie/src/components/inputs/Checkbox';
import { z } from 'zod';

import { useFlow } from '../../common/providers/FlowProvider';
import { useFlowRouter } from '../../common/providers/FlowRouter';
import { BaseStep } from '../../common/Step';
import {
    centsToFormattedDollars,
    formatCurrencyInputOnChange,
    formattedDollarsToCents,
} from '../../common/util/currencyUtils';
import { transformEnumToDisplay } from '../../common/util/utils';
import type { AddIncomeFlowSteps } from '../AddIncomeFlow';
import IncomePayCycleSelectionSheet from '../sheets/IncomePayCycleSelectionSheet';
import {
    type Income,
    IncomeAdditionalCompensation,
    type IncomePayCycle,
} from '../types/Income';
import { formatPayCycle } from '../utils/incomeUtils';

const fullTimeIncomeDetailsSchema = z.object({
    annualSalary: z.string().min(3), // annualSalary is the formatted string, which includes a left padding of two characters: "$ ".
    monthlyTakeHome: z.string().min(3), // monthlyTakeHome is the formatted string, which includes a left padding of two characters: "$ ".
    payCycle: z.string().min(1, 'Required field'),
    additionalInformation: z.string().optional(),
});

type FullTimeIncomeDetailsSchema = z.infer<typeof fullTimeIncomeDetailsSchema>;

export const FullTimeDetailsStep = () => {
    const { data } = useFlow<Income>();
    const { onContinue } = useFlowRouter<Income, AddIncomeFlowSteps>();

    const [isPayCycleSheetOpen, setIsPayCycleSheetOpen] = useState(false);

    const [additionalCompensation, setAdditionalCompensation] = useState<
        IncomeAdditionalCompensation[]
    >(data.additionalCompensation ?? []);

    const handleSelectAdditionalCompensation = (
        value: IncomeAdditionalCompensation,
    ) => {
        setAdditionalCompensation((prevSelectedOptions) => {
            // Check if the value is already in the selected options.
            if (prevSelectedOptions.includes(value)) {
                // Remove the value if it's already selected.
                return prevSelectedOptions.filter((option) => option !== value);
            } else {
                // Add the value if it's not already selected.
                return [...(prevSelectedOptions ?? []), value];
            }
        });
    };

    const {
        register,
        control,
        handleSubmit,
        setFocus,
        setValue,
        formState: { errors, isValid },
        trigger,
        watch,
    } = useForm<FullTimeIncomeDetailsSchema>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(fullTimeIncomeDetailsSchema),
        defaultValues: {
            annualSalary: centsToFormattedDollars(data.annualSalaryCents),
            monthlyTakeHome: centsToFormattedDollars(data.monthlyTakeHomeCents),
            payCycle: data.payCycle ?? '',
            additionalInformation: data.additionalInformation ?? '',
        },
    });

    const onSubmit: SubmitHandler<FullTimeIncomeDetailsSchema> = async ({
        annualSalary,
        monthlyTakeHome,
        payCycle,
        additionalInformation,
    }: FullTimeIncomeDetailsSchema) => {
        const incomeData = {
            annualSalaryCents: formattedDollarsToCents(annualSalary),
            monthlyTakeHomeCents: formattedDollarsToCents(monthlyTakeHome),
            additionalCompensation,
            payCycle: payCycle as IncomePayCycle,
            additionalInformation,
        };

        await onContinue(incomeData);
    };

    const payCycle = watch('payCycle');

    return (
        <BaseStep
            onContinue={handleSubmit(onSubmit)}
            isContinueDisabled={!isValid}
        >
            <YStack gap={12}>
                <Form onSubmit={handleSubmit(onSubmit)} marginBottom={12}>
                    <YStack gap={12}>
                        <ControlledInput
                            label="Annual salary"
                            name="annualSalary"
                            type="numeric"
                            inputMode="numeric"
                            control={control}
                            register={register}
                            errors={errors}
                            enterKeyHint="next"
                            formatValueOnChange={formatCurrencyInputOnChange}
                            onSubmitEditing={() => {
                                setFocus('monthlyTakeHome');
                            }}
                        />
                        <ControlledInput
                            label="Monthly take-home (approximate)"
                            name="monthlyTakeHome"
                            type="numeric"
                            inputMode="numeric"
                            control={control}
                            register={register}
                            errors={errors}
                            enterKeyHint="next"
                            formatValueOnChange={formatCurrencyInputOnChange}
                        />
                        <ControlledInputTrigger
                            label="Pay cycle"
                            name="payCycle"
                            formatValue={formatPayCycle}
                            control={control}
                            register={register}
                            errors={errors}
                            onTrigger={() => {
                                setIsPayCycleSheetOpen(true);
                            }}
                        />
                        <CheckboxRowInsetList
                            title="Additional compensation"
                            items={Object.values(
                                IncomeAdditionalCompensation,
                            ).map((value) => ({
                                id: value,
                                label: transformEnumToDisplay(value),
                                isSelected:
                                    additionalCompensation.includes(value),
                                onPress: () => {
                                    handleSelectAdditionalCompensation(value);
                                },
                            }))}
                        />
                        <ControlledMultilineInput
                            label="Additional information (optional)"
                            name="additionalInformation"
                            control={control}
                            register={register}
                            errors={errors}
                            enterKeyHint="done"
                            blurOnSubmit={true}
                            onSubmitEditing={() => {
                                Keyboard.dismiss();
                            }}
                        />
                    </YStack>
                </Form>
            </YStack>
            <IncomePayCycleSelectionSheet
                isOpen={isPayCycleSheetOpen}
                selectedOption={payCycle as IncomePayCycle}
                onSelectOption={(payCycle: IncomePayCycle | null) => {
                    if (payCycle != null) {
                        setValue('payCycle', payCycle);
                        void trigger('payCycle'); // Manually trigger form validation after setValue
                    }
                }}
                onClose={() => {
                    setIsPayCycleSheetOpen(false);
                }}
            />
        </BaseStep>
    );
};
